import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

// function injectMegaMenuPromoBox() {
//   const jWidget = document.querySelector('.j-widget');
//   if (!jWidget || jWidget.querySelector('.mega-menu-promo-box')) return;
//   const widget = document.querySelector('fieldset.j-widget');
//   const promoBox = document.createElement('a');
 
  

//   const promo = document.createElement('div');
//   promo.className = 'mega-menu-promo-box';
//   promo.textContent = 'SAVE 10%';

//   jWidget.appendChild(promo);

// }

// setTimeout(injectMegaMenuPromoBox, 3000);

// document.addEventListener(
//   'pointerenter',
//   (event) => {
//     if (event.target instanceof Element && event.target.closest('.menu-list__list-item')) {
//       injectMegaMenuPromoBox();
//     }
//   },
//   true
// );
// const style = document.createElement('style');

// style.textContent = `
//   .mega-menu-promo-box {
//       position: absolute;
//   top: -28px;
//   right: 0;
//   background: #e6b1b1;
//   color: #000;
//   padding: 9px 16px;
//   font-size: 11px;
//   font-weight: 700;
//   letter-spacing: 0.08em;
//   text-transform: uppercase;
//   z-index: 2;
//   }

//   .mega-menu-promo-box__image {
//     position: relative;
//     margin-bottom: 12px;
//     overflow: hidden;
//   }

//   .mega-menu-promo-box__image img {
//     width: 100%;
//     display: block;
//     aspect-ratio: 5 / 3;
//     object-fit: cover;
//   }

//   .mega-menu-promo-box__flag {
//     position: absolute;
//     top: 0;
//     right: 0;
//     z-index: 1;
//     display: inline-block;
//     background: #ecd6d0;
//     color: #232323;
//     font-size: 10px;
//     font-weight: 700;
//     letter-spacing: 0.08em;
//     line-height: 1;
//     text-transform: uppercase;
//     padding: 8px 12px;
//     border-radius: 0;
//   }

//   .mega-menu-promo-box__content h3 {
//     margin: 0 0 6px;
//     font-size: 14px;
//     font-weight: 500;
//     line-height: 1.3;
//   }

//   .mega-menu-promo-box__content p {
//     margin: 0;
//     font-size: 12px;
//     line-height: 1.4;
//     color: inherit;
//   }
// `;

// document.head.appendChild(style);